import {ProductionRadar} from '../programming/ProductionRadar';
import {equipmentConfirmationQueue,equipmentQueueLabel} from '../equipmentConfirmationQueue';
import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { normalizeStaffRole } from '../staffRoles';
import { crewRole, crewArrivalDeadline, hasOperationalRole } from '../crewChecklist';
import { FileText, Calendar, DollarSign, Target, Plus, HardHat, AlertCircle, CheckCircle, Clock, BarChart3, Briefcase, FolderOpen } from 'lucide-react';
import { GlassCard } from './ui/GlassCard';
import { Button } from './ui/Button';
import { PipelineItem, Quote } from '../types';
import { supabase } from '../supabaseClient';
import { FinancialPacing } from './FinancialPacing';

interface DashboardProps {
  crmItems: PipelineItem[];
  quotes: Quote[];
  staffList: any[];
  companies: any[];
  allShifts?: any[]; 
  onQuoteAction: (item: PipelineItem) => void;
  onViewInquiry: (item: PipelineItem) => void;
  onChangeTab: (tab: string) => void;
  isLoading: boolean;
  onAddInquiry?: () => void;
  onAddTask?: () => void;
  tasks?: any[];
  currentUser?: any;
  onEditTask?: (task: any) => void;
  onSetLeadFilter: (filter: string) => void;
  onCompleteTask?: (task: any) => void; // ⚡ NEW PROP
}

export const DashboardOverview = ({ 
  crmItems = [], 
  quotes = [], 
  staffList = [],
  allShifts = [], // ⚡ NOW IT RECEIVES THE SHIFT DATA!
  onViewInquiry, 
  onChangeTab,
  isLoading,
  onAddInquiry,
  onAddTask,
  tasks = [],
  currentUser,
  onEditTask,
  onSetLeadFilter,
  onCompleteTask, // ⚡ ADD HERE
}: DashboardProps) => {

  const [financialTarget, setFinancialTarget] = useState(() => {
    const saved = localStorage.getItem('quikpix_financial_target');
    return saved ? parseInt(saved) : 500000;
  });

  const handleUpdateTarget = () => {
    const userInput = window.prompt("Enter your new annual financial target (numbers only):", financialTarget.toString());
    if (userInput && !isNaN(Number(userInput))) {
        const newTarget = Number(userInput);
        setFinancialTarget(newTarget);
        localStorage.setItem('quikpix_financial_target', newTarget.toString());
    }
  };

  const [activeTaskFilter, setActiveTaskFilter] = useState('ALL');
  
  // ⚡ NEW: State to track the Network Feed filter
  const [activeFeedFilter, setActiveFeedFilter] = useState('ALL'); 

  // ⚡ 1. ADD LOCAL STATE AND REALTIME LISTENER FOR SHIFTS ⚡
  const [liveShifts, setLiveShifts] = useState(allShifts);

  useEffect(() => {
      setLiveShifts(allShifts);
  }, [allShifts]);

  useEffect(() => {
      const channel = supabase
          .channel('dashboard-live-tracker')
          .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'event_shifts' }, (payload) => {
              // Instantly inject the contractor's update into the dashboard without a hard refresh
              setLiveShifts(prevShifts => 
                  prevShifts.map(shift => shift.id === payload.new.id ? payload.new : shift)
              );
          })
          .subscribe();

      return () => {
          supabase.removeChannel(channel);
      };
  }, []);
  // ⚡ END OF LISTENER ⚡

  // ⚡ NEW: THE LIVE DISPATCH ENGINE
  const liveDispatchFeed = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const dispatchCards: any[] = [];

    liveShifts.forEach(shift => {
        if (!shift.event_date) return;
        const shiftDate = new Date(shift.event_date);
        shiftDate.setHours(0, 0, 0, 0);

        const diffTime = shiftDate.getTime() - today.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        // Skip jobs in the past
        if (diffDays < 0) return;

        // Skip jobs way in the future (Only track today, tomorrow, and the next day)
        if (diffDays > 2) return;

        const roster = shift.staff_roster;
        if (!roster || !roster.crew || roster.crew.length === 0) return;

        const parentLead = crmItems.find(i => i.id === shift.lead_id);
        if (!parentLead) return;

        const stage = String((parentLead as any).status || parentLead.salesStage || parentLead.productionStage || '').toUpperCase();
        if (['LOST', 'COMPLETED', 'COMPLETE', 'REJECTED', 'WITHDRAWN', 'CANCELED'].includes(stage)) return;

        // Map through each crew member on this shift
        const activeCrewMembers = roster.crew.filter((c: any) => c.status === 'CONFIRMED' || c.status === 'ACCEPTED');
        if (activeCrewMembers.length === 0) return;

        activeCrewMembers.forEach((member: any) => {
            const progress = member.mission_progress || [];
            
            // IF THE JOB IS IN THE FUTURE: Only show it if they've actually started packing (Peace of Mind feature)
            if (diffDays > 0 && progress.length === 0) return;

            // Determine their current status label
            let currentStatus = { label: 'Awaiting Action', color: 'bg-slate-800 text-slate-400 border-slate-700' };
            let isLate = false;

            if (progress.includes('SHIFT_END') || progress.includes('GEAR_RETURNED')) {
                currentStatus = { label: 'Shift Completed', color: 'bg-green-500/10 text-green-400 border-green-500/30' };
            } else if (progress.includes('PACK_DOWN')) {
                currentStatus = { label: 'Packing Down', color: 'bg-orange-500/10 text-orange-400 border-orange-500/30' };
            } else if (progress.includes('EVENT_LIVE')) {
                currentStatus = { label: 'Live on Site', color: 'bg-brand-neonBlue/10 text-brand-neonBlue border-brand-neonBlue/30' };
            } else if (progress.includes('SETUP_COMPLETE')) {
                currentStatus = { label: 'Setup Completed', color: 'bg-brand-neonBlue/10 text-brand-neonBlue border-brand-neonBlue/30' };
            } else if (progress.includes('ARRIVED')) {
                currentStatus = { label: 'Arrived at Venue', color: 'bg-brand-neonPink/10 text-brand-neonPink border-brand-neonPink/30' };
            } else if (progress.includes('EN_ROUTE')) {
                currentStatus = { label: 'In Transit', color: 'bg-brand-neonPink/10 text-brand-neonPink border-brand-neonPink/30' };
            } else if (progress.includes('GEAR_LOADED')) {
                currentStatus = { label: 'Gear Packed', color: 'bg-yellow-500/10 text-yellow-500 border-yellow-500/30' };
            }

            // CHECK LATE STATUS (Only if the job is today and they haven't arrived yet)
            const arrivalTime = crewArrivalDeadline(crewRole(member.role, staffList.find(s => s.id === member.id)?.role), member.call_time, shift.setup_time, shift.start_time);
            if (diffDays === 0 && !progress.includes('ARRIVED') && arrivalTime) {
                const now = new Date();
                const [setupH, setupM] = arrivalTime.split(':').map(Number);
                const setupTimeObj = new Date();
                setupTimeObj.setHours(setupH, setupM, 0, 0);

                if (now > setupTimeObj) {
                    currentStatus = { label: 'LATE FOR CALL TIME', color: 'bg-red-500 text-white border-red-400 shadow-[0_0_15px_rgba(239,68,68,0.8)] animate-pulse' };
                    isLate = true;
                }
            }

            if (!hasOperationalRole(crewRole(member.role, staffList.find(s => s.id === member.id)?.role))) {
                currentStatus = { label: 'Operational role required', color: 'bg-yellow-500/10 text-yellow-500 border-yellow-500/30' };
                isLate = false;
            }
            dispatchCards.push({
                shiftId: shift.id,
                crewId: member.id,
                eventName: shift.shift_title || parentLead.eventName || 'Unnamed Event',
                crewName: member.name,
                role: member.role,
                status: currentStatus,
                isLate: isLate,
                dateLabel: diffDays === 0 ? 'Today' : diffDays === 1 ? 'Tomorrow' : 'Upcoming'
            });
        });
    });

    return dispatchCards.sort((a, b) => {
            if (a.isLate) return -1;
            if (b.isLate) return 1;
            return 0;
        });
    }, [liveShifts, crmItems, staffList]);
    
    // --- Advanced Data Intelligence (Financials & Logistics) ---
  const stats = useMemo(() => {
    const safeQuotes = quotes || [];
    const safeCrmItems = crmItems || [];

    const getQuoteValue = (q: any) => Number(q.subtotal || q.value || q.amount || 0);

    // 1. FINANCIAL PIPELINE (⚡ PERFECTLY SYNCED WITH PIPELINE BOARD MATH)
    const draftQuotes = safeQuotes.filter(q => q.status === 'DRAFT');
    const draftValue = draftQuotes.reduce((sum, q) => sum + getQuoteValue(q), 0);

    let totalRevenue = 0; 
    let pipelineValue = 0; 
    let wonCount = 0;
    let lostCount = 0;

    const wonLeads: any[] = [];
    const pendingLeads: any[] = [];

    safeCrmItems.forEach(item => {
        const stage = String((item as any).stage || item.productionStage || item.salesStage || (item as any).status || '').toUpperCase();
        const val = Number(item.value || 0);
        
        // Match Pipeline Rules: Filter out jobs where all quotes were rejected/withdrawn
        const leadQuotes = safeQuotes.filter(q => q.linkedCrmItemId === item.id || (q as any).lead_id === item.id);
        const hasQuotes = leadQuotes.length > 0;
        const hasActiveQuotes = leadQuotes.some(q => !['WITHDRAWN', 'REJECTED', 'ARCHIVED'].includes(String(q.status).toUpperCase()));
        
        if (hasQuotes && !hasActiveQuotes && stage !== 'LOST' && stage !== 'WITHDRAWN') return;

        if (['PRE_PRODUCTION', 'STAGING', 'LIVE_EVENT', 'POST_EVENT', 'COMPLETED', 'COMPLETE', 'APPROVED', 'WON'].includes(stage)) {
            totalRevenue += val;
            wonCount++;
            wonLeads.push(item);
        } else if (['INQUIRY', 'DISCOVERY', 'PROPOSAL'].includes(stage)) {
            pipelineValue += val;
            pendingLeads.push(item);
        } else if (['LOST', 'REJECTED'].includes(stage)) {
            lostCount++;
        }
    });

    const decidedCount = wonCount + lostCount;
    const winRate = decidedCount > 0 ? Math.round((wonCount / decidedCount) * 100) : 0;
    const avgDealSize = wonCount > 0 ? Math.round(totalRevenue / wonCount) : 0;

    const months = ['Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan', 'Feb', 'Mar'];
    const today = new Date();
    
    let fyStartYear = today.getFullYear();
    if (today.getMonth() < 3) fyStartYear -= 1;

    // ⚡ PROJECTIONS NOW USE THE SAME LEAD-BASED MATH
    const fullYearProjection = months.map((monthStr, index) => {
      const actualMonthIndex = (index + 3) % 12; 
      const actualYear = actualMonthIndex < 3 ? fyStartYear + 1 : fyStartYear;

      const isLeadInMonth = (item: any) => {
        const trueEventDate = item.eventDate || (item as any).event_date || item.createdAt || (item as any).created_at;
        const qDate = new Date(trueEventDate || new Date());
        return qDate.getMonth() === actualMonthIndex && qDate.getFullYear() === actualYear;
      };

      const confirmedRevenue = wonLeads.filter(isLeadInMonth).reduce((sum, item) => sum + Number(item.value || 0), 0);
      const pendingRevenue = pendingLeads.filter(isLeadInMonth).reduce((sum, item) => sum + Number(item.value || 0), 0);

      return { 
        month: monthStr, 
        confirmed: confirmedRevenue, 
        pending: pendingRevenue, 
        total: confirmedRevenue + pendingRevenue,
        isCurrentMonth: today.getMonth() === actualMonthIndex && today.getFullYear() === actualYear
      };
    });
    
    const monthlyTarget = financialTarget / 12;
    const maxProjectedRevenue = Math.max(...fullYearProjection.map(d => d.total), monthlyTarget * 1.1, 1);

    const leadCount = safeCrmItems.filter(i => {
        const stage = String((i as any).status || (i as any).salesStage || '').toUpperCase();
        return stage === 'INQUIRY' || stage === 'DISCOVERY' || stage === 'NEW';
    }).length;

    // ⚡ LOGISTICS ENGINE
    const activeProjectMap = new Map();
    safeCrmItems.forEach(i => {
        const stage = String((i as any).stage || i.productionStage || i.salesStage || (i as any).status || '').toUpperCase();
        if (['APPROVED', 'WON', 'PRE_PRODUCTION', 'STAGING', 'LIVE_EVENT', 'ONBOARDING', 'UPCOMING'].includes(stage)) {
            activeProjectMap.set(i.id, i);
        }
    });

    const unassignedShifts: any[] = [];
    const pendingApprovals: any[] = [];
    const awaitingResponses: any[] = [];
    const unconfirmedGear = equipmentConfirmationQueue(safeCrmItems, allShifts);

    allShifts.forEach(shift => {
        const parentProject = activeProjectMap.get(shift.lead_id);
        if (!parentProject) return; 

        const shiftContextObject = { 
            ...parentProject, 
            shift_id: shift.id,
            shift_title: shift.shift_title, 
            shift_date: shift.event_date 
        };

        // Confirmation state is authoritative. A confirmed empty list means no
        // equipment is required and must not return to the review queue.


        const roster = shift.staff_roster;
        const hasNoRoster = !roster || !roster.crew || roster.crew.length === 0;

        let hasPending = false, hasAccepted = false, hasUnfilled = true;
        
        if (!hasNoRoster) {
            const confirmedCount = roster.crew.filter((c: any) => c.status === 'CONFIRMED').length;
            hasUnfilled = confirmedCount < (roster.required_count || 1);
            hasPending = roster.crew.some((c: any) => c.status === 'PENDING');
            hasAccepted = roster.crew.some((c: any) => c.status === 'ACCEPTED');
        }
                            
        if (hasNoRoster || hasUnfilled) {
            unassignedShifts.push(shiftContextObject);
        }
        if (hasAccepted) {
            pendingApprovals.push(shiftContextObject);
        }
        if (hasPending) {
            awaitingResponses.push(shiftContextObject);
        }
    });

    const threeDaysAgo = new Date();
    threeDaysAgo.setDate(threeDaysAgo.getDate() - 3);

    const draftsOpen = draftQuotes.length;
    const sentQuotes = safeQuotes.filter(q => q.status === 'SENT' || String(q.status) === 'PROPOSAL');
    const toFollowUp = sentQuotes.filter(q => {
      const d = new Date((q as any).updated_at || q.createdAt || (q as any).created_at || new Date());
      return d < threeDaysAgo;
    }).length;

    const needsQuote = safeCrmItems.filter(item => {
        const stage = String((item as any).status || (item as any).salesStage || '').toUpperCase();
        if (stage !== 'INQUIRY' && stage !== 'DISCOVERY') return false;
        const hasQuote = safeQuotes.some(q => q.linkedCrmItemId === item.id || (q as any).lead_id === item.id);
        return !hasQuote;
    }).length;

    return { 
      totalRevenue, pipelineValue, draftValue, winRate, wonCount, lostCount, avgDealSize,
      leadCount, sentCount: sentQuotes.length, draftCount: draftQuotes.length,
      unassignedShifts, pendingApprovals, awaitingResponses, unconfirmedGear,
      fullYearProjection, maxProjectedRevenue,
      draftsOpen, toFollowUp, needsQuote 
    };
  }, [crmItems, quotes, allShifts, financialTarget]);

  // ⚡ MASTER HYBRID ENGINE: Combines live shifts (protects MoE tours) AND master placeholders (protects Open Country) while gating out Discovery!
  const upcomingShiftsFeed = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const nextWeek = new Date(today);
    nextWeek.setDate(today.getDate() + 7);

    const feedItems: any[] = [];
    const coveredShiftLeads = new Set<string>();

    // Step 1: Process actual shifts first (Ensures multi-day expo shifts show up independently!)
    allShifts.forEach(shift => {
       if (!shift.event_date) return;
       const shiftDate = new Date(shift.event_date);
       if (isNaN(shiftDate.getTime())) return;
       shiftDate.setHours(0,0,0,0);

       // Check if this specific shift date lands in our 7-day window
       if (shiftDate < today || shiftDate > nextWeek) return;

       const parentProject = crmItems.find(i => i.id === shift.lead_id);
       if (!parentProject) return;

       // Filter out lost, completed, or unapproved pipelines
       const stage = String((parentProject as any).stage || parentProject.productionStage || parentProject.salesStage || (parentProject as any).status || '').toUpperCase();
       
       // ⚡ UPDATED: Removed INQUIRY/DISCOVERY so internal rosters show up if a shift is created!
       const isLostOrDone = ['LOST', 'COMPLETED', 'COMPLETE', 'REJECTED', 'WITHDRAWN', 'CANCELED'].includes(stage);
       if (isLostOrDone) return;

       const roster = shift.staff_roster;
       const hasNoRoster = !roster || !roster.crew || roster.crew.length === 0;
       const confirmedCount = hasNoRoster ? 0 : roster.crew.filter((c: any) => c.status === 'CONFIRMED').length;
       const needsCrew = hasNoRoster || confirmedCount < (roster.required_count || 1);

       feedItems.push({
           ...parentProject,
           shift_id: shift.id,
           shift_title: shift.shift_title || parentProject.eventName,
           shift_date: shift.event_date,
           shift_venue: shift.venue_location || parentProject.eventVenue,
           needs_crew: needsCrew
       });
       
       // Mark this project ID as handled by an explicit shift
       coveredShiftLeads.add(shift.lead_id);
    });

    // Step 2: Catch approved master jobs that DO NOT have a shift row initialized yet (Protects Open Country placeholder!)
    crmItems.forEach(item => {
       if (coveredShiftLeads.has(item.id)) return; // Skip if already mapped in Step 1

       const stage = String((item as any).stage || item.productionStage || item.salesStage || (item as any).status || '').toUpperCase();
       const isApprovedJob = ['APPROVED', 'WON', 'PRE_PRODUCTION', 'STAGING', 'LIVE_EVENT', 'ONBOARDING', 'UPCOMING'].includes(stage);
       
       // ⚡ NEW: Bypasses the sales gate if the job is explicitly marked as an internal expo or meeting
       const isInternalOps = item.eventName?.toLowerCase().includes('internal') || item.eventName?.toLowerCase().includes('meetings');
       
       if (!isApprovedJob && !isInternalOps) return;

       const trueDateStr = item.eventDate || (item as any).event_date;
       if (!trueDateStr) return;

       const eventDate = new Date(trueDateStr);
       if (isNaN(eventDate.getTime())) return;
       eventDate.setHours(0,0,0,0);

       if (eventDate < today || eventDate > nextWeek) return;

       feedItems.push({
           ...item,
           shift_id: item.id,
           shift_title: item.eventName || (item as any).project_ref || 'Untitled Event',
           shift_date: trueDateStr,
           shift_venue: item.eventVenue || (item as any).venue_location || 'Venue TBD',
           needs_crew: false
       });
    });

    // Step 3: Sort the entire combined list chronologically
    return feedItems.sort((a: any, b: any) => new Date(a.shift_date).getTime() - new Date(b.shift_date).getTime());
  }, [allShifts, crmItems]);

  const upcomingNetworkItems = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    return [...crmItems]
      .filter(item => {
         const stage = String((item as any).status || item.productionStage || item.salesStage || '').toUpperCase();
         const isLostOrDone = ['LOST', 'COMPLETED', 'COMPLETE', 'REJECTED', 'WITHDRAWN', 'CANCELED'].includes(stage);
         if (isLostOrDone) return false;
         if (!item.eventDate || item.eventDate === 'TBD') return false;
         
         const itemDate = new Date(item.eventDate);
         if (isNaN(itemDate.getTime())) return false;
         itemDate.setHours(0,0,0,0);
         
         // ⚡ NEW: Feed Filter Logic
         if (activeFeedFilter !== 'ALL') {
             if (stage !== activeFeedFilter) return false;
         }
         
         return itemDate >= today;
      })
      .sort((a, b) => new Date(a.eventDate!).getTime() - new Date(b.eventDate!).getTime());
  }, [crmItems, activeFeedFilter]); // ⚡ Added activeFeedFilter to dependencies

  if (isLoading) {
    return (
      <div className="space-y-8 animate-pulse">
        <div className="h-16 bg-slate-900/40 rounded-2xl border border-white/5" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map(i => <div key={i} className="h-24 bg-slate-900/40 rounded-2xl border border-white/5" />)}
        </div>
      </div>
    );
  }

  const handleQuoteWarp = (targetStatus: string) => {
    onChangeTab('quotes');
    setTimeout(() => {
        const selects = Array.from(document.querySelectorAll('select'));
        const quoteSelect = selects.find(s => s.innerHTML.includes('Drafts Only'));
        if (quoteSelect) {
            const setter = Object.getOwnPropertyDescriptor(window.HTMLSelectElement.prototype, 'value')?.set;
            if (setter) {
                setter.call(quoteSelect, targetStatus);
                quoteSelect.dispatchEvent(new Event('change', { bubbles: true }));
            }
        }
    }, 50);
  };

  return (
    <div className="animate-fade-in-up space-y-6 pb-12">
      
      {/* ⚡ THE LIVE DISPATCH WIDGET ⚡ */}
      {liveDispatchFeed.length > 0 && (
          <div className="mb-6 md:mb-8">
              <div className="flex items-center gap-2 mb-3 px-1">
                  <div className="w-2 h-2 bg-brand-neonPink rounded-full shadow-neon-pink animate-pulse"></div>
                  <h3 className="type-heading text-base text-white md:text-xl">Live Event Tracker</h3>
              </div>
              <div className="flex overflow-x-auto hide-scrollbar gap-3 pb-2 snap-x -mx-4 px-4 md:mx-0 md:px-0">
                  {liveDispatchFeed.map((card, idx) => (
                      <div key={`${card.shiftId}-${card.crewId}-${idx}`} className="w-[260px] md:w-[320px] bg-slate-900/80 border border-white/10 p-4 md:p-5 rounded-2xl md:rounded-[24px] shrink-0 snap-start flex flex-col justify-between shadow-lg relative overflow-hidden group hover:border-brand-neonBlue/40 transition-all">
                          <div className="absolute top-0 right-0 w-20 h-20 bg-brand-neonBlue/10 blur-[30px] rounded-full pointer-events-none group-hover:bg-brand-neonBlue/20 transition-all"></div>
                          <div className="relative z-10">
                              <div className="flex justify-between items-start mb-2.5">
                                  <span className="type-label text-slate-500">{card.dateLabel}</span>
                                  <span className={`type-label rounded border px-2 py-1 ${card.status.color}`}>
                                      {card.status.label}
                                  </span>
                              </div>
                              <h4 className="text-sm md:text-base font-bold text-white leading-tight mb-3 line-clamp-2">{card.eventName}</h4>
                          </div>
                          <div className="flex items-center gap-3 pt-3 border-t border-white/5 relative z-10">
                              <div className="w-8 h-8 rounded-full bg-slate-800 text-slate-400 font-bold flex items-center justify-center text-xs border border-white/10 shrink-0">
                                  {card.crewName.substring(0, 2).toUpperCase()}
                              </div>
                              <div className="min-w-0">
                                  <p className="text-white font-bold text-xs md:text-sm truncate">{card.crewName}</p>
                                  <p className="truncate text-xs font-medium text-slate-500">{normalizeStaffRole(card.role)}</p>
                              </div>
                          </div>
                      </div>
                  ))}
              </div>
          </div>
      )}

      {/* ⚡ COMMAND BAR */}
      <div className="grid grid-cols-2 lg:flex lg:flex-row gap-2 md:gap-3 mb-8 w-full">
        <button onClick={onAddInquiry} className="type-button flex min-h-11 w-full items-center justify-center gap-1.5 rounded-xl border border-white/10 bg-slate-900/40 px-2 py-3 text-xs text-slate-300 shadow-sm transition-all hover:border-brand-neonBlue hover:bg-brand-neonBlue/10 hover:text-brand-neonBlue">
          <Plus size={14} className="opacity-70 shrink-0"/> <span className="truncate mt-0.5">Log Lead</span>
        </button>
        <button onClick={() => onChangeTab('quotes')} className="type-button flex min-h-11 w-full items-center justify-center gap-1.5 rounded-xl border border-white/10 bg-slate-900/40 px-2 py-3 text-xs text-slate-300 shadow-sm transition-all hover:border-brand-neonBlue hover:bg-brand-neonBlue/10 hover:text-brand-neonBlue">
          <FileText size={14} className="opacity-70 shrink-0"/> <span className="truncate mt-0.5">New Proposal</span>
        </button>
        <button onClick={onAddTask} className="type-button flex min-h-11 w-full items-center justify-center gap-1.5 rounded-xl border border-white/10 bg-slate-900/40 px-2 py-3 text-xs text-slate-300 shadow-sm transition-all hover:border-brand-neonPink hover:bg-brand-neonPink/10 hover:text-brand-neonPink">
          <CheckCircle size={14} className="opacity-70 shrink-0"/> <span className="truncate mt-0.5">Add Task</span>
        </button>
        <button onClick={() => onChangeTab('calendar')} className="type-button flex min-h-11 w-full items-center justify-center gap-1.5 rounded-xl border border-white/10 bg-slate-900/40 px-2 py-3 text-xs text-slate-300 shadow-sm transition-all hover:border-white hover:bg-white/10 hover:text-white">
          <Calendar size={14} className="opacity-70 shrink-0"/> <span className="truncate mt-0.5">Live Schedule</span>
        </button>
      </div>

      {/* ⚡ UNIFIED ACTION CENTER */}
      <div className="grid grid-cols-1 sm:grid-cols-2 2xl:grid-cols-4 gap-4 md:gap-6 pb-4" aria-label="Dashboard radars">
          
          <div className="min-w-0 bg-slate-900/60 border border-white/10 rounded-2xl md:rounded-[24px] p-4 shadow-xl flex flex-col relative overflow-hidden group">
              <div className="absolute top-0 right-0 w-24 h-24 bg-brand-neonBlue/10 blur-[40px] rounded-full pointer-events-none group-hover:bg-brand-neonBlue/20 transition-all"></div>
              <div className="flex items-center gap-3 mb-4 relative z-10">
                  <div className="w-8 h-8 rounded-lg bg-slate-800 flex items-center justify-center text-brand-neonBlue border border-brand-neonBlue/20">
                      <Target size={16} />
                  </div>
                  <div>
                      <h4 className="text-white font-bold text-sm tracking-tight leading-none">Sales Radar</h4>
                      <p className="text-[9px] text-slate-500 uppercase tracking-widest font-black mt-1">Revenue Pipeline</p>
                  </div>
              </div>
              <div className="mt-auto relative z-10 flex flex-col gap-2">
              {stats.needsQuote > 0 && (
                      <Button onClick={() => { onSetLeadFilter('NEEDS_QUOTE'); onChangeTab('leads'); }} className="w-full bg-slate-800 hover:bg-brand-neonBlue/20 text-white border border-white/5 hover:border-brand-neonBlue/30 font-bold text-xs py-2 justify-start transition-all">
                          <span className="w-2 h-2 rounded-full bg-brand-neonBlue mr-2 shadow-[0_0_10px_rgba(0,255,255,0.6)]"></span>
                          {stats.needsQuote} Needs Quote
                      </Button>
                  )}
                  {stats.draftsOpen > 0 && (
                      <Button onClick={() => handleQuoteWarp('DRAFT')} className="w-full bg-slate-800 hover:bg-yellow-500/20 text-white border border-white/5 hover:border-yellow-500/30 font-bold text-xs py-2 justify-start transition-all">
                          <span className="w-2 h-2 rounded-full bg-yellow-500 mr-2"></span>
                          {stats.draftsOpen} Drafts Open
                      </Button>
                  )}
                  {stats.toFollowUp > 0 && (
                      <Button onClick={() => handleQuoteWarp('FOLLOW_UP')} className="w-full bg-slate-800 hover:bg-orange-500/20 text-white border border-white/5 hover:border-orange-500/30 font-bold text-xs py-2 justify-start transition-all">
                          <span className="w-2 h-2 rounded-full bg-orange-500 mr-2 shadow-[0_0_10px_rgba(249,115,22,0.6)] animate-pulse"></span>
                          {stats.toFollowUp} To Follow-Up
                      </Button>
                  )}
                  {stats.needsQuote === 0 && stats.draftsOpen === 0 && stats.toFollowUp === 0 && (
                      <div className="w-full bg-white/5 border border-white/5 text-slate-500 font-bold text-xs py-2 rounded-lg flex items-center justify-center uppercase tracking-widest cursor-pointer hover:bg-white/10 transition-colors" onClick={() => onChangeTab('quotes')}>
                          Pipeline Clear
                      </div>
                  )}
              </div>
          </div>

          <div className="min-w-0 bg-slate-900/60 border border-white/10 rounded-2xl md:rounded-[24px] p-4 shadow-xl flex flex-col relative overflow-hidden group">
              <div className="absolute top-0 right-0 w-24 h-24 bg-brand-neonPink/10 blur-[40px] rounded-full pointer-events-none group-hover:bg-brand-neonPink/20 transition-all"></div>
              <div className="flex items-center gap-3 mb-4 relative z-10">
                  <div className="w-8 h-8 rounded-lg bg-slate-800 flex items-center justify-center text-brand-neonPink border border-brand-neonPink/20">
                      <HardHat size={16} />
                  </div>
                  <div>
                      <h4 className="text-white font-bold text-sm tracking-tight leading-none">Logistics Radar</h4>
                      <p className="text-[9px] text-slate-500 uppercase tracking-widest font-black mt-1">Crew & Deployment</p>
                  </div>
              </div>
              <div className="mt-auto relative z-10 flex flex-col gap-2">
                  {/* ⚡ NEW: GEAR TO CONFIRM ALERT */}
                  {stats.unconfirmedGear.length > 0 && (
                      <Button onClick={() => { onSetLeadFilter('NEEDS_GEAR'); onChangeTab('leads'); }} className="w-full bg-slate-800 hover:bg-orange-500/20 text-white border border-white/5 hover:border-orange-500/30 font-bold text-xs py-2 justify-start transition-all">
                          <span className="w-2 h-2 rounded-full bg-orange-500 mr-2 shadow-[0_0_10px_rgba(249,115,22,0.6)] animate-pulse"></span>
                          {equipmentQueueLabel(stats.unconfirmedGear)}
                      </Button>
                  )}
                  {/* ⚡ NOW COUNTS INDIVIDUAL SHIFTS PROPERLY! */}
                  {stats.unassignedShifts.length > 0 && (
                      <Button onClick={() => { onSetLeadFilter('SHORT_STAFFED'); onChangeTab('leads'); }} className="w-full bg-slate-800 hover:bg-rose-500/20 text-white border border-white/5 hover:border-rose-500/30 font-bold text-xs py-2 justify-start transition-all">
                          <span className="w-2 h-2 rounded-full bg-rose-500 mr-2 shadow-[0_0_10px_rgba(244,63,94,0.6)] animate-pulse"></span>
                          {stats.unassignedShifts.length} Short Staffed
                      </Button>
                  )}
                  {stats.pendingApprovals.length > 0 && (
                      <Button onClick={() => { onSetLeadFilter('TO_APPROVE'); onChangeTab('leads'); }} className="w-full bg-slate-800 hover:bg-brand-neonBlue/20 text-white border border-white/5 hover:border-brand-neonBlue/30 font-bold text-xs py-2 justify-start transition-all">
                          <span className="w-2 h-2 rounded-full bg-brand-neonBlue mr-2"></span>
                          {stats.pendingApprovals.length} To Approve
                      </Button>
                  )}
                  {stats.awaitingResponses.length > 0 && (
                      <Button onClick={() => { onSetLeadFilter('AWAITING_REPLY'); onChangeTab('leads'); }} className="w-full bg-slate-800 hover:bg-yellow-500/20 text-white border border-white/5 hover:border-yellow-500/30 font-bold text-xs py-2 justify-start transition-all">
                          <span className="w-2 h-2 rounded-full bg-yellow-500 mr-2"></span>
                          {stats.awaitingResponses.length} Awaiting Reply
                      </Button>
                  )}
                  {stats.unassignedShifts.length === 0 && stats.pendingApprovals.length === 0 && stats.awaitingResponses.length === 0 && stats.unconfirmedGear.length === 0 && (
                      <div className="w-full bg-white/5 border border-white/5 text-slate-500 font-bold text-xs py-2 rounded-lg flex items-center justify-center uppercase tracking-widest cursor-pointer hover:bg-white/10 transition-colors" onClick={() => { onSetLeadFilter('ACTIVE'); onChangeTab('leads'); }}>
                          Rosters & Gear Set
                      </div>
                  )}
              </div>
          </div>

          <div className="min-w-0 bg-slate-900/60 border border-white/10 rounded-2xl md:rounded-[24px] p-4 shadow-xl flex flex-col relative overflow-hidden group">
              <div className="absolute top-0 right-0 w-24 h-24 bg-purple-500/10 blur-[40px] rounded-full pointer-events-none group-hover:bg-purple-500/20 transition-all"></div>
              <div className="flex items-center gap-3 mb-4 relative z-10">
                  <div className="w-8 h-8 rounded-lg bg-slate-800 flex items-center justify-center text-purple-400 border border-purple-500/20">
                      <CheckCircle size={16} />
                  </div>
                  <div>
                      <h4 className="text-white font-bold text-sm tracking-tight leading-none">Task Control</h4>
                      <p className="text-[9px] text-slate-500 uppercase tracking-widest font-black mt-1">Global Operations</p>
                  </div>
              </div>
              <div className="mt-auto relative z-10 flex flex-col gap-2">
                  {(() => {
                      const dueTasks = tasks.filter(t => (t.status === 'TODO' || t.status === 'PENDING') && new Date(t.due_date || new Date()) <= new Date()).length;
                      if (dueTasks > 0) {
                          return (
                              <Button onClick={() => onChangeTab('tasks')} className="w-full bg-slate-800 hover:bg-purple-500/20 text-white border border-white/5 hover:border-purple-500/30 font-bold text-xs py-2 justify-start transition-all">
                                  <span className="w-2 h-2 rounded-full bg-purple-500 mr-2 shadow-[0_0_10px_rgba(168,85,247,0.6)] animate-pulse"></span>
                                  {dueTasks} Tasks Due
                              </Button>
                          );
                      }
                      return (
                          <div className="w-full bg-white/5 border border-white/5 text-slate-500 font-bold text-xs py-2 rounded-lg flex items-center justify-center uppercase tracking-widest cursor-pointer hover:bg-white/10 transition-colors" onClick={() => onChangeTab('tasks')}>
                              Zero Pending
                          </div>
                      );
                  })()}
              </div>
          </div>
          <ProductionRadar jobs={crmItems} staff={staffList} onOpen={id=>{const job=crmItems.find(j=>j.id===id);if(job)onViewInquiry({...job,programmingFocus:true} as PipelineItem);}}/>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 xl:gap-8">
        
        {/* ⚡ LEFT: 7-DAY OUTLOOK */}
        <div className="lg:col-span-4 space-y-6 order-2 lg:order-1">
          <div className="bg-slate-900/40 rounded-[24px] border border-white/5 p-6 relative overflow-hidden shadow-2xl h-full">
          <div className="flex items-center justify-between mb-6">
              <h4 className="text-base xl:text-lg font-bold text-white flex items-center gap-2">
                <Calendar className="text-brand-neonBlue w-4 h-4" /> Outlook
              </h4>
              <span className="type-label rounded-md bg-white/5 px-2 py-1 text-white/40">Next 7 Days</span>
            </div>
            
            {/* ⚡ THE FIX: Added max-h-[400px] and custom-scrollbar here! */}
            <div className="space-y-3 max-h-[400px] overflow-y-auto custom-scrollbar pr-2 pb-2">
              {upcomingShiftsFeed.length === 0 ? (
                 <div className="p-8 text-center text-slate-700 italic uppercase tracking-[0.2em] font-black opacity-40 border-2 border-dashed border-white/5 rounded-[20px]">
                   Schedule Clear
                 </div>
              ) : (
                upcomingShiftsFeed.map((shift: any) => {
                  const today = new Date();
                  today.setHours(0,0,0,0);
                  const itemDate = new Date(shift.shift_date!);
                  itemDate.setHours(0,0,0,0);
                  const diffTime = itemDate.getTime() - today.getTime();
                  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                  
                  let dateLabel = '';
                  if (diffDays === 0) dateLabel = 'Today';
                  else if (diffDays === 1) dateLabel = 'Tomorrow';
                  else dateLabel = itemDate.toLocaleDateString('en-NZ', { weekday: 'short', month: 'short', day: 'numeric' });

                  return (
                    <div 
                      key={shift.shift_id}
                      onClick={() => onViewInquiry(shift)}
                      className={`p-4 rounded-xl bg-slate-900/50 border hover:border-brand-neonBlue/30 transition-all group cursor-pointer ${shift.needs_crew ? 'border-red-500/20 hover:border-red-500/50' : 'border-white/5'}`}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div className="min-w-0 pr-2">
                          <div className={`type-label mb-1 ${diffDays === 0 ? 'text-brand-neonPink' : diffDays === 1 ? 'text-brand-neonBlue' : 'text-white/50'}`}>
                            {dateLabel}
                          </div>
                          <div className={`text-sm font-bold text-white transition-colors truncate ${shift.needs_crew ? 'group-hover:text-red-400' : 'group-hover:text-brand-neonBlue'}`}>
                            {shift.shift_title}
                          </div>
                        </div>
                        <span className={`w-2 h-2 rounded-full shrink-0 ${shift.needs_crew ? 'bg-red-500 shadow-[0_0_10px_rgba(239,68,68,0.6)] animate-pulse' : 'bg-green-500 shadow-[0_0_10px_rgba(34,197,94,0.6)]'}`}></span>
                      </div>
                      
                      <div className="flex flex-wrap items-center gap-2 mt-2">
                        <span className="max-w-full truncate rounded bg-white/10 px-2 py-0.5 text-xs font-medium">
                           {shift.shift_venue || 'Venue TBA'}
                        </span>
                        {shift.needs_crew && (
                          <span className="type-label shrink-0 rounded border border-red-500/20 bg-red-500/10 px-2 py-0.5 text-red-400">
                            Crew Unassigned
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>

        {/* ⚡ MIDDLE: NETWORK ACTIVITY */}
        <div className="lg:col-span-4 space-y-6 order-1 lg:order-2 flex flex-col h-full">
          <div className="flex justify-between items-end px-1 shrink-0">
            <div>
              <h3 className="text-lg xl:text-xl font-bold text-white flex items-center gap-2">Network Feed</h3>
              <p className="type-label mt-1 flex items-center gap-2 text-slate-500">
                <span className="w-1.5 h-1.5 rounded-full bg-brand-neonBlue animate-pulse" /> Live Deal Activity
              </p>
            </div>
          </div>

          {/* ⚡ NEW: NETWORK FEED FILTERS */}
          <div className="flex gap-2 px-1 overflow-x-auto hide-scrollbar shrink-0">
            {['ALL', 'PROPOSAL', 'PRE_PRODUCTION', 'STAGING'].map(tab => (
              <button
                key={tab}
                onClick={() => setActiveFeedFilter(tab)}
                className={`type-button min-h-11 whitespace-nowrap rounded-lg px-3 py-1.5 text-xs transition-all ${
                  activeFeedFilter === tab
                    ? 'bg-brand-neonBlue/20 text-brand-neonBlue border border-brand-neonBlue/40 shadow-[0_0_10px_rgba(0,255,255,0.2)]'
                    : 'bg-white/5 text-slate-400 border border-white/5 hover:bg-white/10 hover:text-white'
                }`}
              >
                {tab.replace('_', ' ')}
              </button>
            ))}
          </div>

          <div className="space-y-3 max-h-[400px] overflow-y-auto custom-scrollbar pr-2 pb-2 mt-2">
            {upcomingNetworkItems.length > 0 ? upcomingNetworkItems.map((item) => (
              <div 
                key={item.id} 
                className="p-4 rounded-xl bg-slate-900/50 border border-white/5 hover:border-brand-neonBlue/30 transition-all group cursor-pointer flex items-center justify-between shadow-lg"
                onClick={() => onViewInquiry(item)}
              >
                <div className="flex items-center gap-3 min-w-0 pr-2">
                  <div className="w-9 h-9 rounded-lg bg-white/5 flex items-center justify-center text-xs font-bold text-white/40 border border-white/10 group-hover:border-brand-neonBlue/50 group-hover:text-brand-neonBlue transition-colors shrink-0">
                    {(item.firstName?.[0] || '?').toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-bold text-white group-hover:text-brand-neonBlue transition-colors mb-0.5 truncate">
                      {item.eventName || (item as any).project_ref || 'Unnamed Project'}
                    </div>
                    <div className="flex items-center gap-1.5 truncate text-xs font-medium text-white/50">
                      <span className="text-brand-neonBlue">{new Date(item.eventDate!).toLocaleDateString('en-NZ', { month: 'short', day: 'numeric' })}</span> 
                      <span>•</span> 
                      {item.firstName} {item.lastName}
                    </div>
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-sm font-bold text-white mb-0.5 tabular-nums">
                    ${Number(item.value || 0).toLocaleString()}
                  </div>
                  <div className={`type-label ${item.type === 'PRODUCTION' ? 'text-brand-neonPink' : 'text-blue-500'}`}>
                    {item.productionStage || item.salesStage}
                  </div>
                </div>
              </div>
            )) : (
              <div className="p-8 text-center text-slate-700 italic uppercase tracking-[0.2em] font-black opacity-40 border-2 border-dashed border-white/5 rounded-[20px]">
                Feed Empty
              </div>
            )}
          </div>
        </div>

        {/* ⚡ RIGHT: ACTION ITEMS */}
        <div className="lg:col-span-4 space-y-6 order-3 lg:order-3">
          <div className="flex items-center justify-between px-1">
            <div>
              <h3 className="text-lg xl:text-xl font-bold text-white flex items-center gap-2">Action Items</h3>
              <p className="type-label mt-1 text-slate-500">Task Control</p>
            </div>
            {(() => {
              const pendingCount = tasks.filter(t => t.status === 'TODO' || t.status === 'PENDING').length;
              if (pendingCount > 0) {
                  return (
                    <button onClick={() => onChangeTab('tasks')} className="type-label min-h-11 rounded-md border border-red-500/20 bg-red-500/10 px-2.5 py-1 text-red-400 transition-colors hover:text-red-300">
                      {pendingCount} Due
                    </button>
                  );
              }
              return (
                  <span className="type-label rounded-md border border-green-500/20 bg-green-500/10 px-2.5 py-1 text-green-500">
                    Clear
                  </span>
              );
            })()}
          </div>
          <div className="flex gap-2 px-1 overflow-x-auto hide-scrollbar">
            {['ALL', 'MY TASKS', 'EVENT', 'ADMIN'].map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTaskFilter(tab)}
                className={`type-button min-h-11 rounded-lg px-3 py-1.5 text-xs transition-all ${
                  activeTaskFilter === tab
                    ? 'bg-brand-neonBlue/20 text-brand-neonBlue border border-brand-neonBlue/40 shadow-[0_0_10px_rgba(0,255,255,0.2)]'
                    : 'bg-white/5 text-slate-400 border border-white/5 hover:bg-white/10 hover:text-white'
                }`}
              >
                {tab}
              </button>
            ))}
          </div>
          
          <div className="space-y-3 max-h-[400px] overflow-y-auto custom-scrollbar pr-2 pb-2">
            {(() => {
              const today = new Date();
              today.setHours(0, 0, 0, 0);

              const pendingTasks = tasks
                .filter(t => t.status === 'TODO' || t.status === 'PENDING')
                .filter(t => {
                  if (activeTaskFilter === 'MY TASKS') return currentUser && t.assignee_id === currentUser.id;
                  if (activeTaskFilter === 'EVENT') return t.lead_id !== null;
                  if (activeTaskFilter === 'ADMIN') return t.project_id !== null;
                  return true; 
                })
                .sort((a, b) => new Date(a.due_date || '2099-01-01').getTime() - new Date(b.due_date || '2099-01-01').getTime());

              if (pendingTasks.length === 0) {
                return (
                  <div className="p-8 text-center text-slate-700 italic uppercase tracking-[0.2em] font-black opacity-40 border-2 border-dashed border-white/5 rounded-[20px]">
                    No Pending Tasks
                  </div>
                );
              }

              return pendingTasks.map(task => {
                let diffDays = 999;
                let isOverdue = false, isToday = false, isTomorrow = false;
                let dateLabel = 'No Date';

                if (task.due_date) {
                  const dueDate = new Date(task.due_date);
                  dueDate.setHours(0, 0, 0, 0);
                  const diffTime = dueDate.getTime() - today.getTime();
                  diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                  
                  isOverdue = diffDays < 0;
                  isToday = diffDays === 0;
                  isTomorrow = diffDays === 1;

                  if (isOverdue) dateLabel = `Overdue: ${Math.abs(diffDays)} Day${Math.abs(diffDays) > 1 ? 's' : ''}`;
                  else if (isToday) dateLabel = 'Due: Today';
                  else if (isTomorrow) dateLabel = 'Due: Tomorrow';
                  else dateLabel = `Due: ${dueDate.toLocaleDateString('en-NZ', { month: 'short', day: 'numeric' })}`;
                }

                return (
                  <div 
                    key={task.id} 
                    onClick={() => onEditTask ? onEditTask(task) : onChangeTab('tasks')} 
                    className={`p-4 rounded-xl bg-slate-900/50 border hover:border-brand-neonBlue/40 transition-all group flex gap-3 cursor-pointer shadow-lg ${isOverdue ? 'border-red-500/20' : 'border-white/5'}`}
                  >
                    {/* ⚡ NEW: INTERACTIVE CHECKBOX */}
                    <button 
                        onClick={(e) => {
                            e.stopPropagation();
                            if (onCompleteTask) onCompleteTask(task);
                        }}
                        title="Mark as Complete"
                        className={`mt-0.5 w-4 h-4 rounded border-2 flex-shrink-0 transition-all duration-200 cursor-pointer relative z-20 hover:!bg-green-500 hover:!border-green-500 hover:shadow-[0_0_12px_rgba(34,197,94,0.6)] ${
                            isOverdue ? 'border-red-500/50' : 
                            isToday ? 'border-brand-neonBlue/50' : 
                            'border-white/20'
                        }`}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-bold text-white group-hover:text-brand-neonBlue transition-colors leading-tight mb-1 truncate">
                        {task.title}
                      </div>
                      
                      {/* ⚡ NEW: PROJECT CONTEXT BADGES */}
                      <div className="flex flex-wrap items-center gap-2 mb-1.5">
                          {task.lead_id && (() => {
                              const lead = crmItems.find(i => i.id === task.lead_id);
                              if (!lead) return null;
                              return (
                                  <span className="text-[9px] font-black uppercase tracking-widest text-brand-neonBlue bg-brand-neonBlue/10 border border-brand-neonBlue/20 px-2 py-0.5 rounded-md flex items-center gap-1 truncate max-w-[200px]">
                                      <Briefcase size={10} className="shrink-0" /> {lead.eventName}
                                  </span>
                              );
                          })()}
                          {task.project_id && (
                              <span className="text-[9px] font-black uppercase tracking-widest text-purple-400 bg-purple-500/10 border border-purple-500/20 px-2 py-0.5 rounded-md flex items-center gap-1 truncate max-w-[200px]">
                                  <FolderOpen size={10} className="shrink-0" /> Internal Project
                              </span>
                          )}
                      </div>

                      <div className={`text-[9px] uppercase tracking-widest font-bold flex items-center gap-1.5 ${isOverdue ? 'text-red-400' : isToday ? 'text-brand-neonBlue' : isTomorrow ? 'text-yellow-400' : 'text-slate-500'}`}>
                        {(isOverdue || isToday) && (
                          <span className={`w-1.5 h-1.5 rounded-full ${isOverdue ? 'bg-red-500 animate-pulse' : 'bg-brand-neonBlue'}`}></span>
                        )}
                        {dateLabel}
                      </div>
                    </div>
                  </div>
                );
              });
            })()}
          </div>
        </div>

      </div>

      {/* --- SECTION 3: FINANCIALS & PACING --- */}
      <div className="pt-4 border-t border-white/10 mt-8">
        
        <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
            <DollarSign className="text-green-500" /> Financial Performance
        </h3>

        <div className="flex overflow-x-auto hide-scrollbar gap-4 snap-x snap-mandatory pb-4 -mx-4 px-4 md:mx-0 md:px-0 md:grid md:grid-cols-2 lg:grid-cols-3">
        
        {/* REVENUE */}
        <GlassCard className="min-w-[280px] md:min-w-0 shrink-0 snap-center transform-gpu relative z-10 p-4 md:p-5 border-white/5 bg-slate-900/40 hover:border-brand-neonBlue/30 transition-all duration-300 flex flex-col justify-center">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-brand-neonBlue/10 text-brand-neonBlue shadow-inner border border-brand-neonBlue/20 shrink-0">
              <DollarSign size={20} />
            </div>
            <div className="min-w-0 flex-1">
              <p className="type-label mb-0.5 text-slate-500">Total Revenue (Ex-GST)</p>
              <h2 className="type-numeric text-xl leading-none text-white xl:text-2xl">
                ${stats.totalRevenue.toLocaleString(undefined, {maximumFractionDigits: 0})}
              </h2>
              <p className="text-[10px] text-slate-400 mt-1 font-medium truncate">
                Avg Deal: <span className="text-brand-neonBlue">${stats.avgDealSize.toLocaleString(undefined, {maximumFractionDigits: 0})}</span>
              </p>
            </div>
          </div>
        </GlassCard>

        {/* PIPELINE VALUE */}
        <GlassCard className="min-w-[280px] md:min-w-0 shrink-0 snap-center transform-gpu relative z-10 p-4 md:p-5 border-white/5 bg-slate-900/40 hover:border-purple-500/30 transition-all duration-300 flex flex-col justify-center">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-purple-500/10 text-purple-400 shadow-inner border border-purple-500/20 shrink-0">
              <FileText size={20} />
            </div>
            <div className="min-w-0 flex-1">
              <p className="type-label mb-0.5 truncate text-slate-500">Active Pipeline (Ex-GST)</p>
              <h2 className="type-numeric truncate text-xl leading-none text-white xl:text-2xl">
                ${stats.pipelineValue.toLocaleString(undefined, {maximumFractionDigits: 0})}
              </h2>
              <p className="text-[10px] text-slate-400 mt-1 font-medium truncate">
                Drafts: <span className="text-slate-300">${stats.draftValue.toLocaleString(undefined, {maximumFractionDigits: 0})}</span>
              </p>
            </div>
          </div>
        </GlassCard>

        {/* WIN/LOSS BAR */}
        <GlassCard className="min-w-[280px] md:min-w-0 shrink-0 snap-center transform-gpu relative z-10 p-4 md:p-5 border-white/5 bg-slate-900/40 hover:border-green-500/30 transition-all duration-300 flex flex-col justify-center">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-green-500/10 text-green-500 shadow-inner border border-green-500/20 shrink-0">
              <Target size={20} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex justify-between items-end mb-1.5">
                 <p className="type-label truncate text-slate-500">Win Rate</p>
                 <span className="text-sm font-bold text-white leading-none">{stats.winRate}%</span>
              </div>
              <div className="h-1.5 w-full bg-slate-800 rounded-full overflow-hidden flex">
                 <div style={{width: `${stats.winRate}%`}} className="bg-green-500 h-full shadow-[0_0_10px_rgba(34,197,94,0.5)] transition-all duration-1000"></div>
                 <div style={{width: `${100 - stats.winRate}%`}} className="bg-rose-500/50 h-full transition-all duration-1000"></div>
              </div>
              <p className="text-[9px] text-slate-400 mt-1.5 font-medium truncate">
                <span className="text-green-400">{stats.wonCount} Won</span> <span className="mx-1">•</span> <span className="text-rose-400">{stats.lostCount} Lost</span>
              </p>
            </div>
          </div>
        </GlassCard>

      </div>

      <div className="mt-8 flex overflow-x-auto hide-scrollbar gap-6 snap-x snap-mandatory pb-4 -mx-4 px-4 md:mx-0 md:px-0 md:flex-col md:overflow-visible">
        
        {/* CHART 1: FULL YEAR PIPELINE BAR CHART */}
        <div className="min-w-[90vw] sm:min-w-[80vw] md:min-w-0 shrink-0 snap-center">
           <GlassCard className="p-6 md:p-8 border-white/10 bg-slate-900/60 w-full shadow-xl">
             <div className="flex justify-between items-start mb-8">
               <div>
                  <h3 className="text-white font-bold text-xl md:text-2xl flex items-center gap-3">
                    <BarChart3 size={24} className="text-brand-neonBlue" /> Revenue Projection
                  </h3>
                  <p className="text-slate-500 text-[10px] md:text-xs font-black uppercase tracking-widest mt-2">FY Monthly Breakdown (Ex-GST)</p>
               </div>
             </div>
             
             <div className="relative flex items-end justify-between h-[200px] md:h-[240px] gap-2 md:gap-4 mt-4 pt-4">
                
                <div 
                    className="absolute left-0 right-0 border-t-2 border-dashed border-slate-500/50 z-0 pointer-events-none"
                    style={{ bottom: `${( (financialTarget / 12) / stats.maxProjectedRevenue) * 100}%` }}
                >
                    <span className="absolute -top-6 right-0 text-[9px] font-black uppercase tracking-widest text-slate-400 bg-slate-900/90 px-2 py-1 rounded-md border border-white/5 shadow-lg">
                        Target: ${(financialTarget / 12).toLocaleString(undefined, {maximumFractionDigits: 0})}
                    </span>
                </div>

                {stats.fullYearProjection.map((data) => {
                  const confirmedPct = data.total > 0 ? (data.confirmed / stats.maxProjectedRevenue) * 100 : 0;
                  const pendingPct = data.total > 0 ? (data.pending / stats.maxProjectedRevenue) * 100 : 0;
                  
                  return (
                     <div key={data.month} className="flex flex-col items-center flex-1 group relative h-full justify-end z-10">
                       
                       <div className="absolute -top-12 bg-slate-800 text-white text-[9px] font-bold py-1.5 px-2 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-20 shadow-xl border border-white/10 flex flex-col gap-0.5">
                         <span className="text-brand-neonBlue">Won: ${data.confirmed.toLocaleString()}</span>
                         <span className="text-yellow-500">Pending: ${data.pending.toLocaleString()}</span>
                       </div>
                       
                       <div className="w-full bg-slate-950/50 rounded-t-md border-b border-white/5 flex flex-col justify-end overflow-hidden h-full mt-auto relative">
                          {data.total === 0 && (
                             <div style={{ height: '5%' }} className="w-full bg-slate-800/50 rounded-t-sm transition-all duration-1000 group-hover:bg-slate-700"></div>
                          )}
                          
                          {pendingPct > 0 && (
                            <div 
                              style={{ height: `${pendingPct}%` }} 
                              className="w-full bg-yellow-500/40 border-t border-x border-yellow-500/50 transition-all duration-1000 group-hover:bg-yellow-500/60 rounded-t-sm"
                            ></div>
                          )}
                          
                          {confirmedPct > 0 && (
                            <div 
                              style={{ height: `${confirmedPct}%` }} 
                              className={`w-full transition-all duration-1000 ${data.pending === 0 ? 'rounded-t-sm' : ''} ${data.isCurrentMonth ? 'bg-brand-neonBlue shadow-[0_0_15px_rgba(0,255,255,0.4)]' : 'bg-brand-neonBlue/70 group-hover:bg-brand-neonBlue'}`}
                            ></div>
                          )}
                       </div>
                       
                       <span className={`text-[8px] md:text-[10px] font-black uppercase tracking-widest mt-3 ${data.isCurrentMonth ? 'text-brand-neonBlue' : 'text-slate-500'}`}>
                         {data.month}
                       </span>
                     </div>
                  )
                })}
             </div>
           </GlassCard>
        </div>

        {/* CHART 2: PACING LINE CHART */}
        <div className="min-w-[90vw] sm:min-w-[80vw] md:min-w-0 shrink-0 snap-center relative group w-full">
           <FinancialPacing 
              quotes={crmItems.map((item: any) => {
                  // 1. Get the current master stage
                  const stage = String(item.stage || item.productionStage || item.salesStage || item.status || '').toUpperCase();
                  
                  // 2. Exact match for the Top Card's exclusion logic
                  const leadQuotes = quotes.filter(q => q.linkedCrmItemId === item.id || (q as any).lead_id === item.id);
                  const hasQuotes = leadQuotes.length > 0;
                  const hasActiveQuotes = leadQuotes.some(q => !['WITHDRAWN', 'REJECTED', 'ARCHIVED'].includes(String(q.status).toUpperCase()));
                  
                  // If all quotes were rejected, hide it from the graph
                  if (hasQuotes && !hasActiveQuotes && stage !== 'LOST' && stage !== 'WITHDRAWN') {
                      return { 
                          status: 'REJECTED', 
                          subtotal: 0, 
                          eventDate: null,
                          createdAt: item.createdAt || item.created_at 
                      }; 
                  }

                  // 3. Translate the Lead's stage into the graph's language
                  let graphStatus = 'DRAFT'; // Default (Graph ignores this)
                  if (['PRE_PRODUCTION', 'STAGING', 'LIVE_EVENT', 'POST_EVENT', 'COMPLETED', 'COMPLETE', 'APPROVED', 'WON'].includes(stage)) {
                      graphStatus = 'APPROVED'; // Forces it into the solid "Actuals" line
                  } else if (['INQUIRY', 'DISCOVERY', 'PROPOSAL'].includes(stage)) {
                      graphStatus = 'SENT'; // Forces it into the dotted "Pipeline" line
                  }

                  // 4. Force-feed the single source of truth price
                  const truePrice = Number(item.value || 0);

                  return {
                      ...item, // ⚡ THE FIX: Ensure all original timestamp data passes through to the chart!
                      id: item.id,
                      status: graphStatus,
                      subtotal: truePrice,
                      value: truePrice,
                      amount: truePrice,
                      eventDate: item.eventDate || item.event_date || item.createdAt || item.created_at,
                      createdAt: item.createdAt || item.created_at,
                      created_at: item.createdAt || item.created_at
                  };
              })} 
              annualTarget={financialTarget} 
           />
            
            <button 
                onClick={handleUpdateTarget}
                className="absolute top-6 right-6 md:top-8 md:right-8 opacity-0 group-hover:opacity-100 transition-opacity bg-slate-900/80 hover:bg-slate-800 text-slate-400 hover:text-brand-neonBlue border border-white/10 px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest flex items-center gap-2 backdrop-blur-md z-10"
            >
                ⚙️ Set Target
            </button>
        </div>

      </div>

      {/* ⚡ URGENT TASK SCREEN LOCK ALARM ⚡ */}
        {(() => {
          const urgentTasksDue = tasks.filter(t => 
              t.is_urgent && 
              (t.status === 'TODO' || t.status === 'PENDING') && 
              new Date(t.due_date || new Date()) <= new Date()
          );

          if (urgentTasksDue.length === 0) return null;

          const currentAlarm = urgentTasksDue[0];

          // ⚡ WRAPPED IN A PORTAL TO BREAK OUT OF ANIMATION CAGES
          return createPortal(
              <div role="alert" aria-label="Critical task reminder" className="fixed bottom-4 right-4 z-[99999] w-[calc(100%-2rem)] max-w-md pointer-events-none">
                  <div className="pointer-events-auto bg-red-950/95 backdrop-blur-2xl border-2 border-red-500 shadow-[0_0_60px_rgba(239,68,68,0.35)] rounded-3xl p-6 text-center animate-fade-in-up">
                      <div className="w-16 h-16 bg-red-500/20 text-red-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-inner border border-red-500/50 animate-pulse">
                          <AlertCircle size={32} />
                      </div>
                      <h2 className="type-heading text-3xl md:text-4xl text-white mb-4">
                          CRITICAL ACTION DUE
                      </h2>
                      <p className="text-xl text-red-400 font-bold mb-2">
                          {currentAlarm.title}
                      </p>
                      <p className="text-slate-400 text-sm mb-6 leading-relaxed font-medium">
                          {currentAlarm.description || "This task was marked as critically urgent. Please action it or snooze it when appropriate; CRM navigation remains available."}
                      </p>

                      <div className="flex flex-col gap-4">
                          <Button 
                              onClick={async () => {
                                  if (onCompleteTask) onCompleteTask(currentAlarm);
                              }}
                              className="w-full py-5 bg-green-500 text-slate-950 rounded-2xl font-black uppercase tracking-widest text-sm shadow-[0_0_20px_rgba(34,197,94,0.4)] hover:scale-105 border-none"
                          >
                              <CheckCircle size={18} className="mr-2"/> I Have Completed This
                          </Button>
                          
                          <Button 
                              onClick={async () => {
                                  const tomorrow = new Date();
                                  tomorrow.setDate(tomorrow.getDate() + 1);
                                  await supabase.from('tasks').update({ due_date: tomorrow.toISOString() }).eq('id', currentAlarm.id);
                              }}
                              className="w-full py-5 bg-slate-900 hover:bg-slate-800 text-slate-300 border border-white/10 rounded-2xl font-bold uppercase tracking-widest text-xs"
                          >
                              <Clock size={16} className="mr-2 text-yellow-500"/> Snooze for 24 Hours
                          </Button>
                      </div>
                  </div>
              </div>,
              document.body // ⚡ TELLS THE BROWSER TO MOUNT THIS AT THE HIGHEST LEVEL
          );
        })()}

      </div>

    </div>
  );
};
